/**
 * Q3 — the Resemble rapid-clone quota, which is an OPERATIONAL LIMIT rather
 * than a vanity metric.
 *
 * The rapid-clone allowance is account-wide. A three-hundred-guest Platinum
 * wedding can take most of it, and two of them cannot coexist — so this needs
 * to be visible BEFORE the second Platinum sale, not after the support ticket.
 *
 * ⚠️ THE LIMIT COMES FROM THE SERVER AND IS NEVER HARDCODED HERE. The number
 * exists as prose in a migration, in the wedding service and in an alarm
 * script; the admin backend carries the fourth copy deliberately and ships it in
 * the payload. A fifth copy inside a React component would be the one an
 * operator reads and the one nobody remembers to change.
 *
 * ⚠️ "REMAINING" IS AN UPPER BOUND, NOT A BALANCE. Member-side voice clones draw
 * on the same vendor pool and this endpoint cannot see them without guessing at
 * another service's schema. The server says so in `caveat`, and that sentence is
 * rendered verbatim rather than paraphrased — a card that quietly promised a
 * balance would be wrong in exactly the direction that costs money.
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import type { VoiceQuota } from '../weddingApi';
import { shortDate } from '../weddingFormat';

interface Props {
  quota?: VoiceQuota | null;
}

/** One server-shipped definition string, or undefined if this backend has none. */
function defText(definition: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = definition ? definition[key] : undefined;
  return typeof value === 'string' ? value : undefined;
}

const VoiceQuotaCard: React.FC<Props> = ({ quota }) => {
  if (!quota) {
    return (
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Voice clone slots
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The voice quota is not reported by this version of the admin API.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const share = quota.used_share === null || quota.used_share === undefined
    ? null
    : Math.max(0, Math.min(100, quota.used_share * 100));

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Voice clone slots
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Point in time — not scoped to the date filter.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {quota.slots_used.toLocaleString()} of {quota.limit.toLocaleString()} slots used
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          at most {quota.remaining_at_most.toLocaleString()} remaining
        </Typography>

        {share !== null && (
          <LinearProgress
            variant="determinate"
            value={share}
            color={share >= 80 ? 'error' : 'primary'}
            sx={{ height: 8, borderRadius: 4, mb: 2 }}
            aria-label={`${quota.slots_used} of ${quota.limit} voice clone slots used`}
          />
        )}

        {quota.slots_used === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No contributor has recorded a voice clone yet.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip size="small" color="success" label={`${quota.ready.toLocaleString()} ready`} />
            <Chip size="small" variant="outlined" label={`${quota.pending.toLocaleString()} building`} />
            <Tooltip title={defText(quota.definition, 'consented') || 'Consent recorded; nothing sent to the vendor yet.'}>
              <Chip size="small" variant="outlined" label={`${quota.consented.toLocaleString()} consented`} />
            </Tooltip>
            <Chip
              size="small"
              variant={quota.failed > 0 ? 'filled' : 'outlined'}
              color={quota.failed > 0 ? 'error' : 'default'}
              label={`${quota.failed.toLocaleString()} failed`}
            />
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {quota.books_holding_slots.toLocaleString()} book
          {quota.books_holding_slots === 1 ? '' : 's'} holding slots ·{' '}
          {quota.released.toLocaleString()} released
          {quota.newest_profile_at ? ` · newest ${shortDate(quota.newest_profile_at)}` : ''}
        </Typography>

        {/* The caveat is the server's sentence, verbatim. */}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          {quota.caveat}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default VoiceQuotaCard;
